import { Extension } from '@tiptap/vue-3'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import {
  findMentionMatches,
  normalizeMentionNames,
  isPulseWarningMention,
} from '../mentions'

export interface MentionDecorationOptions {
  // Read fresh on each decoration pass so the styling tracks the current props.
  getMentionUsernames: () => string[]
  getPulseUsernames: () => string[]
  isPulseWarningActive: () => boolean
}

const MentionDecoration = Extension.create<MentionDecorationOptions>({
  name: 'mentionDecoration',

  addOptions() {
    return {
      getMentionUsernames: (): string[] => [],
      getPulseUsernames: (): string[] => [],
      isPulseWarningActive: (): boolean => false,
    }
  },

  addProseMirrorPlugins() {
    const { getMentionUsernames, getPulseUsernames, isPulseWarningActive } =
      this.options

    return [
      new Plugin({
        key: new PluginKey('mentionDecoration'),
        props: {
          decorations: (state: EditorState): DecorationSet => {
            const names = getMentionUsernames()
            if (names.length === 0) return DecorationSet.empty

            const known = normalizeMentionNames(names)
            const pulseUsernames = getPulseUsernames()
            const warningActive = isPulseWarningActive()
            const decorations: Decoration[] = []

            state.doc.descendants((node, pos) => {
              if (node.isText && node.text) {
                findMentionMatches(node.text, known).forEach(
                  ({ index, length, name }) => {
                    const from = pos + index
                    const warn = isPulseWarningMention(
                      name,
                      pulseUsernames,
                      warningActive
                    )
                    decorations.push(
                      Decoration.inline(from, from + length, {
                        class: warn
                          ? 'mention mention--pulse-warning'
                          : 'mention',
                        'data-mention-suggestion-char': '@',
                      })
                    )
                  }
                )
                return
              }

              // usernameMention atoms already render as `.mention` chips, so add
              // only the amber modifier.
              if (
                node.type.name === 'usernameMention' &&
                isPulseWarningMention(
                  String(node.attrs.id ?? ''),
                  pulseUsernames,
                  warningActive
                )
              ) {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: 'mention--pulse-warning',
                  })
                )
              }
            })

            return DecorationSet.create(state.doc, decorations)
          },
        },
      }),
    ]
  },
})

export default MentionDecoration
